import { Component, ComponentInterface, Element, h, Host, JSX, Prop, Watch } from '@stencil/core';
import { DEFAULT_WORKBENCH_THEME, MERCEDES_BENZ_LIGHT, WorkbenchTheme } from '../../shared/themes';
import { ImageFit } from './image-fit';
import { Ratio } from './ratio';

/**
 * @slot - contents placed within the element
 */
@Component({
  tag: 'wb-aspect-ratio',
  styleUrl: 'aspect-ratio.scss',
  shadow: true,
})
export class AspectRatioComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** aspect ratio */
  @Prop({ reflect: true })
  public ratio!: Ratio;

  /** image fit */
  @Prop({ reflect: true })
  public imageFit: ImageFit = 'cover';

  /** the colour scheme */
  @Prop({ reflect: true })
  public theme: WorkbenchTheme = DEFAULT_WORKBENCH_THEME;

  @Watch('imageFit')
  public onImageFitChange(): void {
    this.applyStyles();
  }

  private applyStyles = (): void => {
    this.applyTargetStyles();
    this.applyFigcaptionStyles();
  };

  private applyTargetStyles(): void {
    // note: this can select both `img`, `picture > img` , or other contents
    const targetEl = this.el.querySelector<HTMLElement>('img') || (this.el.firstElementChild as HTMLElement);

    if (!targetEl) {
      return;
    }

    targetEl.style.position = 'absolute';
    targetEl.style.top = '0';
    targetEl.style.left = '0';
    targetEl.style.width = '100%';
    targetEl.style.height = '100%';
    targetEl.style.overflow = 'hidden';
    targetEl.style.objectFit = this.imageFit;
    targetEl.style.verticalAlign = 'middle';
  }

  private applyFigcaptionStyles(): void {
    const figcaption = this.el.querySelector<HTMLElement>('figcaption') as HTMLElement;

    if (!figcaption) {
      return;
    }

    figcaption.style.position = 'absolute';
    figcaption.style.insetBlockEnd = '0';
    figcaption.style.paddingInlineStart = '0.5rem';
    figcaption.style.paddingBlockStart = '0.5rem';
    figcaption.style.paddingBlockEnd = '0.5rem';
    figcaption.style.width = '100%';
    if (this.theme === MERCEDES_BENZ_LIGHT) {
      figcaption.style.color = 'var(--wb-grey-20)';
      figcaption.style.background = 'linear-gradient(to top, rgba(255,255,255,0.7) 30%, rgba(255,255,255,0) 100%)';
    } else {
      figcaption.style.color = 'var(--wb-grey-95)';
      figcaption.style.background = 'linear-gradient(to top, rgba(0,0,0,.8) 0%, rgba(0,0,0,0) 100%)';
    }
  }

  public render(): JSX.Element {
    return (
      <Host class="wb-aspect-ratio">
        <slot onSlotchange={this.applyStyles} />
      </Host>
    );
  }
}
